/* eslint-disable @typescript-eslint/naming-convention */
import { DeliveryOption } from '../../models/delivery-options/delivery-option';
import {
  DeliveryOptionSortType,
  DeliveryOptionLabels,
  DeliveryMethod,
} from '../../models/delivery-options/common';
import { IDropdownItem } from '../../common/select/select.component';
import { LastMile } from '../../models/delivery-options/delivery-miles';

export const sortOptions: IDropdownItem<DeliveryOptionSortType>[] = [
  { label: 'delivery_options_sorting_recommended', value: 'recommended' },
  { label: 'delivery_options_sorting_cheapest', value: 'cheapest' },
];

export function getPrice(option?: DeliveryOption) {
  if (!option) return 0;

  const {
    first_miles: firstMiles,
    middle_mile: middleMile,
    last_miles: lastMiles,
    selectedFirstMile,
    selectedLastMile,
  } = option;

  const lastMilePrice = !selectedLastMile
    ? lastMiles.toSorted((a, b) => a.price - b.price).at(0)?.price
    : selectedLastMile.price;

  const firstMilePrice = !selectedFirstMile
    ? firstMiles.toSorted((a, b) => a.price - b.price).at(0)?.price
    : selectedFirstMile.price;

  return (firstMilePrice ?? 0) + middleMile.price + (lastMilePrice ?? 0);
}

export function filterDeliveryOptions(
  options: DeliveryOption[] | undefined,
  filter: DeliveryMethod | undefined
) {
  return !filter
    ? []
    : options?.filter(
        (option) => option.middle_mile.delivery_method === filter
      );
}

export function getDuration({
  first_miles,
  middle_mile,
  last_miles,
}: DeliveryOption) {
  const cheapestLastMile = last_miles
    .toSorted((a, b) => a.price - b.price)
    .at(0);

  const cheapestFirstMile = first_miles
    .toSorted((a, b) => a.price - b.price)
    .at(0);

  return (
    (cheapestFirstMile?.duration ?? 0) +
    middle_mile.duration +
    (cheapestLastMile?.duration ?? 0)
  );
}

export function getFastestByType(
  options: DeliveryOption[] | undefined,
  type: DeliveryMethod
) {
  const fastestByType = filterDeliveryOptions(options, type)
    ?.toSorted((a, b) => getDuration(a) - getDuration(b))
    ?.at(0);

  return fastestByType;
}

export function getPriceRange(
  options: DeliveryOption[] | undefined,
  middleMile: DeliveryMethod
) {
  const filteredOptions = filterDeliveryOptions(options, middleMile);
  const optionsSortedByPrice = filteredOptions?.toSorted(
    (a, b) => getPrice(a) - getPrice(b)
  );
  return {
    min: getPrice(optionsSortedByPrice?.at(0)),
    max: getPrice(optionsSortedByPrice?.at(-1)),
  };
}

export function getDefaultMile(
  miles: LastMile[],
  mode: string = 'cheapest' // "cheapest_address_delivery"
) {
  const sortedLastMiles = miles.toSorted((a, b) => a.price - b.price);
  if (mode === 'cheapest') {
    return sortedLastMiles?.at(0);
  }

  // "cheapest_address_delivery" case
  const cheapestCourier = sortedLastMiles
    .filter((x) => x.service.code === 'courier')
    .at(0);
  return cheapestCourier ?? sortedLastMiles?.at(0);
}

export function assignDelivetyOptionLabels(alloptions: DeliveryOption[]) {
  alloptions.forEach((option) => {
    option.labels = [];
  });

  // Sort Cards by Price
  const optionsSorted = alloptions.toSorted(
    (a, b) => getPrice(a) - getPrice(b)
  );

  const cheapestMeest = optionsSorted.find(
    (o) => o.selectedFirstMile?.carrier.code === 'Meest'
  );

  if (cheapestMeest) {
    cheapestMeest.labels = ['popular'];
  }

  // Best Price Labels
  optionsSorted[0].labels = [...optionsSorted[0].labels!, 'bestprice'];

  // Find 'fastest' option for delivery. Always show. If transit time is the same - show the cheapest option from the fastest.
  const fastestCard = optionsSorted.toSorted(
    (a, b) => getDuration(a) - getDuration(b)
  )[0];

  if (fastestCard) {
    fastestCard.labels = [...fastestCard.labels!, 'fastest'];
  }

  // Find Meest cheapest Air delivey option for 'optimal'. If no Meest - show cheapest Air delivery. If no both variants - don't show
  const sortedAirOptions = optionsSorted.filter(
    (option) => option.middle_mile.delivery_method === 'air'
  );

  if (sortedAirOptions.length > 0) {
    const cheepestAirMeestCard = sortedAirOptions.find(
      (option) => option.selectedFirstMile?.carrier.code === 'Meest'
    );
    if (cheepestAirMeestCard) {
      cheepestAirMeestCard.labels = [
        ...cheepestAirMeestCard.labels!,
        'optimal',
      ];
    } else {
      sortedAirOptions[0].labels = [...sortedAirOptions[0].labels!, 'optimal'];
    }
  }

  // We should remove 'optimal' in case all 4 labels are already assigned
  const optionWith4Labels = alloptions.find(
    (option) => option.labels && option.labels.length === 4
  );

  if (optionWith4Labels) {
    optionWith4Labels.labels?.pop();
  }
}

export function sortDeliveryOptions(
  options: DeliveryOption[] | undefined,
  sortType: DeliveryOptionSortType
): DeliveryOption[] {
  if (!options) return [];

  const optionsSorted = options.toSorted((a, b) => getPrice(a) - getPrice(b));

  if (sortType === 'recommended') {
    const recomendedCards = optionsSorted.toSorted(
      (a, b) => getPriority(a.labels) - getPriority(b.labels)
    );
    return recomendedCards;
  }

  return optionsSorted;
}

const labelPriority: DeliveryOptionLabels[] = [
  'popular',
  'bestprice',
  'fastest',
  'optimal',
];

function getPriority(labels: DeliveryOptionLabels[] | undefined): number {
  for (let i = 0; i < labelPriority.length; i++) {
    if (labels?.includes(labelPriority[i])) {
      return i;
    }
  }
  return labelPriority.length;
}

export const FILTER_ORDER: DeliveryMethod[] = [
  'air',
  'sea',
  'car',
  'expedited',
];
